import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { clientService } from "@/services/clientService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface ClientRef {
  id: string;
  name: string;
  status?: string;
}

interface ClientsInputProps {
  /** Entity's own clients — array of { client: { id, name, status? } } */
  value: { client: ClientRef; note?: string }[];
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (clients: { client: ClientRef; note?: string }[]) => void;
}

export function ClientsInput({ value, state = "editing", label = "Clients", error, onChange }: ClientsInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  const handleChipPress = useCallback(
    (clientId: string) => {
      const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
      const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
      if (currentPanel && currentId) {
        pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
      }
      navigateToEntity("client", clientId);
    },
    [currentParams, pushNavigation, currentEntity, navigateToEntity],
  );

  // Fetch all client options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const clients = await clientService.getClients();
        if (cancelled) return;
        setOptions(
          clients.map((c) => ({
            id: c.id!,
            name: c.name,
            description: c.status ?? undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load clients:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items from value
  const chipItems = useMemo<ChipItem[]>(() => {
    return value.map((cc) => ({
      id: cc.client.id,
      name: cc.client.name,
      readonly: false,
    }));
  }, [value]);

  const handleAdd = useCallback(
    (clientId: string) => {
      if (value.some((cc) => cc.client.id === clientId)) return;
      const opt = options.find((o) => o.id === clientId);
      if (!opt) return;
      onChange?.([...value, { client: { id: opt.id, name: opt.name, status: opt.description } }]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (chipId: string) => {
      onChange?.(value.filter((cc) => cc.client.id !== chipId));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="domain"
      accentTone="emerald"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search clients…"
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onChipPress={handleChipPress}
    />
  );
}
